<template>
  <table-card-layout :title="title">
    <div slot="header" class="md-layout">
      <slot name="header"></slot>
      <md-button
        v-if="configurableHeaders"
        class="md-just-icon md-simple"
        @click="configureVisibleHeaders"
      >
        <md-icon>settings</md-icon>
      </md-button>
    </div>

    <template slot="content">
      <slot name="table" :tableData="pageData">
        <!-- The real table, a md-table or a wrapper. -->
        <component
          :is="tableType"
          key="table"
          :tableHeaders="Object.values(visibleHeaders)"
          :value="loading ? [] : pageData"
          :md-fixed-header="fixedHeader"
          :md-sort.sync="currentSort"
          :md-sort-order.sync="currentSortOrder"
          :md-sort-fn="customSort"
          class="paginated-table table-hover catalog-table"
          :class="localTableClass"
          transition="fade"
          v-bind="tableAttrs"
          v-on="listeners"
        >
          <md-table-toolbar v-if="!hideToolbar">
            <div class="md-layout md-alignment-center-right">
              <div
                v-if="!hidePageSize"
                class="md-layout-item md-size-15 md-xsmall-size-100 md-layout md-alignment-center-space-between"
              >
                <span>Mostrar</span>
                <md-field>
                  <md-select v-model="pagination.perPage" name="pages">
                    <md-option
                      v-for="item in pagination.perPageOptions"
                      :key="item"
                      :label="item"
                      :value="item"
                    >{{ item }}</md-option>
                  </md-select>
                </md-field>
                <span>elementos</span>
              </div>
              <div
                class="md-layout-item md-size-85 md-xsmall-size-100 md-layout md-alignment-center-right"
              >
                <div v-if="!dontUseSearch" style="margin-right: 24px;">
                  <md-field style="width: 320px;">
                    <md-input
                      type="search"
                      clearable
                      :placeholder="searchPlaceholder"
                      v-model="searchQuery"
                      :disabled="loading"
                    ></md-input>
                  </md-field>
                </div>

                <div v-if="$slots['search-toolbar-actions']">
                  <slot name="search-toolbar-actions"></slot>
                </div>
              </div>
            </div>
          </md-table-toolbar>

          <!-- :NOTE: Only applicable to simple table -->
          <!-- <template slot="first-header">
            <slot name="first-header"></slot>
          </template>-->
          <loading-content
            v-if="loading || !pageData.length"
            table
            :loading="loading"
            v-bind="loadingContentProps"
          />
          <template slot="md-table-row" slot-scope="{ item }">
            <slot name="md-table-row" v-bind="{ item }">
              <slot
                name="table-row"
                v-bind="{
                  item,
                  visibleHeaders,
                  tableComponents,
                  tableFilters,
                  allowedActions,
                  rowClass,
                }"
              >
                <table-row
                  v-if="!loading && !!pageData.length"
                  :item="item"
                  :row-class="rowClass ? rowClass(item) : ''"
                  :table-headers="visibleHeaders"
                  :table-components="tableComponents"
                  :table-filters="tableFilters"
                  :actions="allowedActions"
                  :sortable-fields="sortableFields"
                  :selectable="selectable ? 'single' : undefined"
                >
                  <template v-for="(_, slot) of $cellScopes" v-slot:[slot]="scope">
                    <slot :name="slot" v-bind="scope" />
                  </template>
                </table-row>
              </slot>
            </slot>
          </template>
        </component>
      </slot>

      <visible-headers-modal
        v-if="showVisibleHeadersConfig"
        v-model="localTableVisibleHeaders"
        :headers="tableHeaders"
        @close="showVisibleHeadersConfig = false"
      />
    </template>
    <table-pagination
      v-show="!hideFooter"
      slot="footer"
      :page-size="pagination.perPage"
      :table-data="sortedData"
      :paginationInfo.sync="localPaginationInfo"
      :currentPage.sync="pagination.currentPage"
      v-model="pageData"
    />
  </table-card-layout>
</template>

<script>
import TableCardLayout from "./TableCardLayout";
import TablePagination from "./TablePagination";
import TableRow from "./TableRow";
import BaseTable from "./BaseTable";
import VisibleHeadersModal from "./VisibleHeadersModal";
import usesAccountInfoMixin from "@/mixins/usesAccountInfo";
import { CollapseTransition } from "vue2-transitions";
import SearchMixin from "./search";

/**
 * Catalog Table - An useful data table with search, pagination and more.
 * @author Virgilio Vázquez J. <virgilio.vazquez@jbge.com.mx>
 * @version 1.0
 */
export default {
  components: {
    TablePagination,
    TableCardLayout,
    TableRow,
    BaseTable,
    VisibleHeadersModal,
    CollapseTransition,
  },

  mixins: [usesAccountInfoMixin, SearchMixin],

  props: {
    title: String,
    // tableIcon: {
    //   type: String,
    //   default: "list",
    // },
    tableHeaders: {
      type: Object,
      required: true,
      default: () => {},
    },
    tableData: {
      type: Array,
      required: true,
      default: () => [],
    },
    tableClass: {
      type: [Array, Object, String],
      default: () => ["table-striped"],
    },
    actions: Array,
    footerTable: Array,
    tableFilters: {
      type: Object,
      required: false,
      default: () => {},
    },
    tableComponents: {
      type: Object,
      default: () => ({}),
    },

    tableSortableFields: {
      type: Array,
      required: false,
      default: () => [],
    },
    tableCurrentSort: {
      type: [String, Array],
      default: null,
    },
    tableCurrentSortOrder: {
      type: String,
      default: "asc",
    },
    propsToSearch: {
      type: Array,
      required: false,
      default: () => [],
    },
    rowClass: {
      type: Function,
      required: false,
      default: () => {},
    },
    loading: {
      type: Boolean,
      default: false,
    },
    loadingContentProps: {
      type: Object,
      default: () => ({
        emptyLabel: "Nada que mostrar",
        emptyDescription: "Vuelva a intentar más tarde.",
        emptyIcon: "history",
      }),
    },
    dummySorting: {
      type: Boolean,
      default: false,
    },
    // dummySearch: {
    //   type: Boolean,
    //   default: false,
    // },
    pageSize: {
      type: Number,
      default: 10,
    },
    paginationInfo: {
      type: Object,
      default: null,
    },

    // CONFIGURATIONS
    simpleTable: {
      type: Boolean,
      default: false,
    },
    selectable: {
      type: Boolean,
      default: false,
    },
    searchPlaceholder: {
      type: String,
      default: "Buscar en Registros",
    },
    startSorted: {
      type: Boolean,
    },
    tableVisibleHeaders: {
      type: Array,
      default: () => [],
    },
    searchString: {
      type: String,
      default: () => "",
    },

    fixedHeader: {
      type: Boolean,
    },
    largeContent: Boolean,
    disableScroll: {
      type: Boolean,
      default: false,
    },

    // SHOW/HIDE ELEMENTS
    configurableHeaders: Boolean,
    dontUseSearch: Boolean,
    hidePageSize: Boolean,
    hideFooter: Boolean,
    hideToolbar: Boolean,
  },
  computed: {
    /** *
     * Returns a page from the searched data or the whole data. Search is performed in the watch section below
     */
    allowedActions() {
      const actions = this.actions || [];
      return actions
        .filter((action) => {
          const roles = action.roles;

          return (
            !roles || roles.length == 0 || this.userRoleIs(...roles, "ADM")
          );
        })
        .filter(({ ifUserCan }) =>
          !ifUserCan
            ? true
            : Object.entries(ifUserCan).every(([permission, entry]) => {
                let appModels = entry;
                if (!Array.isArray(appModels)) {
                  appModels = [appModels];
                }

                appModels = appModels.map((s) => s.split("/"));

                return appModels.every(([app, model]) =>
                  this.$can(permission, app, model)
                );
              })
        );
    },
    tableAttrs() {
      const {
        title,
        currentSort,
        currentSortOrder,
        propsToSearch,
        loading,
        loadingContentProps,
        dummySearch,
        pageSize,
        paginationInfo,
        simpleTable,
        ...attrs
      } = this.$attrs;
      return attrs;
    },
    tableType() {
      return this.simpleTable ? "base-table" : "md-table";
    },
    sortableFields() {
      const tableSortableFields = this.tableSortableFields;
      return tableSortableFields && tableSortableFields.length
        ? tableSortableFields
        : [];
    },
    sortedData() {
      const {
        searchedData: data,
        currentSort: sortBy,
        dummySorting,
        paginationInfo,
      } = this;

      if (dummySorting || paginationInfo || !sortBy) {
        return data;
      } else {
        const sorted = this.sortArray([...data], sortBy);
        return sorted ? sorted : data;
      }
    },
    listeners() {
      const {
        updatedSearchedData,
        perPageUpdated,
        // searchQueryUpdated,
        updatedQueriedData,
        updatedPage,
        ...listeners
      } = this.$listeners;
      return listeners;
    },
    visibleHeaders() {
      const { tableHeaders } = this;
      let { localTableVisibleHeaders: visHeaders } = this;
      visHeaders =
        visHeaders && !!visHeaders.length
          ? visHeaders
          : Object.keys(tableHeaders);

      const result = {};
      visHeaders.forEach((header) => {
        result[header] = tableHeaders[header];
      });

      return result;
    },
    localTableClass() {
      const { tableClass, disableScroll, largeContent } = this;

      const scrollClass = !disableScroll ? null : "no-scroll-table";
      const largeClass = largeContent ? "table-large-content" : null;
      return [tableClass, scrollClass, largeClass].filter((k) => k).join(" ");
    },
    $cellScopes() {
      const { $scopedSlots } = this;
      return Object.fromEntries(
        Object.entries($scopedSlots).filter(([slot]) => slot.endsWith("-cell"))
      );
    },
  },
  data() {
    const { tableSortableFields: sortableFields, startSorted } = this;
    const tableCurrentSort =
      this.tableCurrentSort ||
      (startSorted && sortableFields.length ? sortableFields[0] : undefined);
    const tableCurrentSortOrder = this.tableCurrentSortOrder;
    return {
      currentSort: tableCurrentSort,
      currentSortOrder: tableCurrentSortOrder,
      pagination: {
        perPage: this.pageSize,
        currentPage: 1,
        perPageOptions: [5, 10, 25, 50],
        total: 0,
      },
      searchQuery: this.searchString,
      pageData: [],
      localTableVisibleHeaders: [],
      localPaginationInfo: this.paginationInfo,
      showVisibleHeadersConfig: false,
    };
  },
  methods: {
    customSort(value) {
      // Dont sort value, instead sort our data and pass that to them
      return value.sort((a, b) => {
        const sortBy = this.currentSort;
        if (this.currentSortOrder === "desc") {
          return a[sortBy].localeCompare(b[sortBy]);
        }
        return b[sortBy].localeCompare(a[sortBy]);
      });
    },
    sortArray(array, sortBy) {
      sortBy = sortBy || this.currentSort;

      if (!sortBy) {
        return array;
      }

      return array.sort((a, b) => {
        const aValue = a[sortBy];
        const bValue = b[sortBy];
        if (!aValue || !bValue) {
          return true;
        }
        try {
          if (this.currentSortOrder === "desc") {
            return aValue.localeCompare(bValue);
          }
          return bValue.localeCompare(aValue);
        } catch (err) {
          return true;
        }
      });
    },
    configureVisibleHeaders() {
      this.showVisibleHeadersConfig = true;
    },
  },
  watch: {
    "pagination.perPage": {
      immediate: true,
      handler(value) {
        this.$emit("updatedPageSize", value);
      },
    },
    "pagination.currentPage": {
      immediate: true,
      handler(value) {
        this.$emit("pageUpdated", value);
      },
    },
    pageData(value) {
      this.$emit("updatedQueriedData", value);
    },

    currentSort(value) {
      this.$emit("updatedCurrentSort", value);
    },

    currentSortOrder(value) {
      this.$emit("updatedcurrentSortOrder", value);
    },

    tableCurrentSort: {
      immediate: false,
      handler(value) {
        this.currentSort = value;
      },
    },

    tableCurrentSortOrder: {
      immediate: true,
      handler(value) {
        this.currentSortOrder = value;
      },
    },
    tableVisibleHeaders: {
      immediate: true,
      handler(value = []) {
        const { tableHeaders } = this;
        this.localTableVisibleHeaders =
          value && !!value.length ? value : Object.keys(tableHeaders);
      },
    },
    paginationInfo(value) {
      this.localPaginationInfo = value;
    },
    localPaginationInfo(value) {
      this.$emit("update:paginationInfo", value);
      const { next, previous } = value || {};
    },
    searchString(value) {
      this.searchQuery = value;
    },
  },
};
</script>

<style lang="css" scoped>
.md-card.table-card {
  z-index: 0;
}

.md-table-toolbar {
  padding: 4px 12px;
}
/* .mt-auto {
  margin-top: -10px !important;
  margin-right: 5px !important;
  margin-left: 5px !important;
} */

/* .action-tooltip {
  font-size: 16px !important;
} */

/* .catalog-table-header {
  margin-top: 6px;
} */
</style>



// WEBPACK FOOTER //
// src/components/Tables/CatalogTable.vue